"use client"

import { useQuery } from "convex/react";
import { DateRange, isValidRange } from "./TimeRangeSelector";
import { api } from "@/convex/_generated/api";
import React from "react";
import { format } from "numerable";
import { LineChart } from "./tremor/line-chart";
import { createPeriodTooltip } from "./KWHCounterGraph";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

export interface DayAheadTableProps {
    range: DateRange | undefined;
}

export const DayAheadTable = ({ range }: DayAheadTableProps) => {
    const prices = useQuery(api.day_ahead.list, {
        period_start: isValidRange(range) ? range.from.getTime() : -Infinity,
        period_end: isValidRange(range) ? (range.to.getTime() + 1000) : Infinity
    });
    const startFormatter = React.useCallback((value: number) => {
        return new Intl.DateTimeFormat("nl-BE", {
            weekday: "long",
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        }).format(value);
    }, []);
    const endFormatter = React.useCallback((value: number) => {
        return new Intl.DateTimeFormat("nl-BE", {
            hour: "numeric",
            minute: "numeric",
        }).format(value);
    }, []);
    const valueFormatter = React.useCallback((value: number | null) => {
        if (value === null) return "null";
        return format(value, "0,000.00 EUR/MWh")
    }, []);

    return (<>
        <LineChart
            className="h-60"
            data={prices ?? []}
            index="period_start"
            categories={["unit_price"]}
            colors={["blue"]}
            valueFormatter={valueFormatter}
            keyFormatter={startFormatter}
            curveType="stepAfter"
            startEndOnly={(prices?.length ?? 0) > 0}
            xAxisDomain={[
                range?.from?.getTime() || (prices?.[0]?.period_start || 0),
                range?.to?.getTime() || (prices?.[prices.length - 1]?.period_end || 0)
            ]}
            showLegend={false}
            yAxisWidth={150}
            intervalType="equidistantPreserveStart"
            customTooltip={createPeriodTooltip({ valueFormatter })}
        />

        <Sheet>
            <SheetTrigger asChild>
                <Button variant="ghost">
                    <span className="text-sm">Toon details</span>
                </Button>
            </SheetTrigger>
            <SheetContent className="min-w-[500px]">
                <SheetTitle></SheetTitle>
                <SheetDescription></SheetDescription>
                <ScrollArea className="h-[calc(100vh-50px)]">
                    <Table className="table-auto">
                        <TableHeader className="sticky top-0 bg-background">
                            <TableRow>
                                <TableHead className="px-4 py-2 text-left">Periode</TableHead>
                                <TableHead className="px-4 py-2 text-right">Prijs (EUR/MWh)</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {
                                prices?.map((price: any) => (
                                    <TableRow key={price.period_start}>
                                        <TableCell className="px-4 py-2">
                                            {new Intl.DateTimeFormat("nl-BE", {
                                                weekday: "long",
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "numeric",
                                                minute: "numeric",
                                            }).format(new Date(price.period_start))} - {new Intl.DateTimeFormat("nl-BE", {
                                                // dateStyle: "short",
                                                timeStyle: "short"
                                            }).format(new Date(price.period_end))}
                                        </TableCell>
                                        <TableCell className="px-4 py-2 text-right">
                                            {format(price.unit_price, "0,000.00")}
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </ScrollArea>
            </SheetContent>
        </Sheet>
    </>)
}